import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { fmtMoney, fmtDate, vehicleLabel } from "@/lib/format";
import { CheckCircle, Clock, ArrowCounterClockwise, WarningCircle, Plus } from "@phosphor-icons/react";
import PlatformReceivablesTab from "@/components/PlatformReceivablesTab";
import PendingCollectionVerifications from "@/components/PendingCollectionVerifications";
import { useAuth } from "@/context/AuthContext";
import BranchFilter from "@/components/BranchFilter";

const STATUS_META = {
  pending: {
    icon: Clock,
    label: "Pending",
    cls: "border-destructive bg-destructive/5 text-destructive",
  },
  short: {
    icon: WarningCircle,
    label: "Short",
    cls: "border-[#E08600] bg-[#FFF6E5] text-[#A35700]",
  },
  received: {
    icon: CheckCircle,
    label: "Received",
    cls: "border-[#00B341] bg-[#00B341]/10 text-[#00B341]",
  },
};

function StatusPill({ status, testId }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 px-2 py-0.5 border ${meta.cls} text-[10px] font-mono uppercase tracking-wider`}
    >
      <Icon size={10} weight="bold" /> {meta.label}
    </span>
  );
}

function StatPill({ label, value, accent, testId }) {
  return (
    <div className={`border p-4 ${accent}`} data-testid={testId}>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-80">{label}</div>
      <div className="font-display text-2xl font-bold mt-2 tabular-nums">{value}</div>
    </div>
  );
}

const AGING_BUCKETS_META = [
  { key: "0-30", label: "0–30 days", cls: "border-[#00B341] bg-[#00B341]/5", pillCls: "bg-[#00B341]/10 text-[#00713a] border-[#00B341]" },
  { key: "31-60", label: "31–60 days", cls: "border-[#FFD700] bg-[#FFFBE5]", pillCls: "bg-[#FFFBE5] text-[#7a5e00] border-[#FFD700]" },
  { key: "61-90", label: "61–90 days", cls: "border-[#E08600] bg-[#FFF6E5]", pillCls: "bg-[#FFF6E5] text-[#A35700] border-[#E08600]" },
  { key: "90+", label: "90+ days", cls: "border-destructive bg-destructive/5", pillCls: "bg-destructive/10 text-destructive border-destructive" },
];

function AgingPill({ days, bucket, testId }) {
  const meta = AGING_BUCKETS_META.find((b) => b.key === bucket) || AGING_BUCKETS_META[0];
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center px-2 py-0.5 border ${meta.pillCls} text-[10px] font-mono tabular-nums tracking-wider`}
    >
      {days}d
    </span>
  );
}

function AgingStrip({ aging, kind }) {
  if (!aging) return null;
  const totalOutstanding = AGING_BUCKETS_META.reduce(
    (sum, b) => sum + (aging[b.key]?.amount || 0),
    0,
  );
  if (totalOutstanding === 0) return null;
  return (
    <div className="border border-canvas-line bg-canvas-card" data-testid={`${kind}-aging-strip`}>
      <div className="px-3 py-2 border-b border-canvas-line font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted flex items-center justify-between">
        <span>Aging — Outstanding by Days Since Sale</span>
        <span className="text-ink">Total {fmtMoney(totalOutstanding)}</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-canvas-line">
        {AGING_BUCKETS_META.map((b) => {
          const data = aging[b.key] || { count: 0, amount: 0 };
          return (
            <div
              key={b.key}
              data-testid={`${kind}-aging-${b.key}`}
              className={`bg-canvas-card p-3 border-l-4 ${b.cls}`}
            >
              <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">
                {b.label}
              </div>
              <div className="font-display text-lg font-bold text-ink mt-1 tabular-nums">
                {fmtMoney(data.amount)}
              </div>
              <div className="font-mono text-[10px] text-ink-subtle mt-0.5">
                {data.count} {data.count === 1 ? "sale" : "sales"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReceiveDialog({ row, kind, onClose, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const today = new Date().toISOString().slice(0, 10);
  const expected = kind === "finance" ? row.finance_amount : row.exchange_amount;
  const already = row.amount_received || 0;
  const outstanding = Math.max(expected - already, 0);
  const [amount, setAmount] = useState(outstanding > 0 ? String(outstanding) : "");
  const [date, setDate] = useState(today);
  const [ref, setRef] = useState("");
  const [method, setMethod] = useState("cash");  // exchange only — see below
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a positive amount.");
      return;
    }
    if (kind === "exchange" && method !== "cash" && !ref.trim()) {
      toast.error(`${method.toUpperCase()} needs a bank reference / UTR.`);
      return;
    }
    setSaving(true);
    try {
      const path =
        kind === "finance"
          ? `/accounts/finance-receivables/${row.sale_id}/receive`
          : `/accounts/exchange-receivables/${row.sale_id}/receive`;
      const body = {
        amount: amt,
        received_on: date,
        bank_reference: ref,
      };
      if (kind === "exchange") body.payment_method = method;
      await api.post(path, body);
      if (kind === "exchange" && method !== "cash") {
        toast.success("Receipt recorded — awaiting verification in Accounts → Pending Due Verifications");
      } else {
        toast.success(amt >= outstanding ? "Marked received" : "Top-up booked");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const isTopUp = already > 0;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        data-testid="receive-dialog"
        className="bg-canvas-card border-2 border-ink p-6 w-full max-w-md space-y-4"
      >
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">
            {kind === "finance" ? "Finance Receivable" : "Exchange Receivable"}
            {isTopUp ? " · Top-up" : ""}
          </div>
          <h3 className="font-display text-lg font-semibold text-ink mt-1">
            {kind === "finance" ? row.financier_name : row.exchange_description || row.exchange_make_model || "Exchange"} ·{" "}
            {fmtMoney(expected)}
          </h3>
          <div className="font-mono text-[11px] text-ink-subtle mt-1">
            {row.customer?.name || "—"} · Already received: {fmtMoney(already)} · Outstanding:{" "}
            {fmtMoney(outstanding)}
          </div>
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
            Amount Received Now (₹)
          </label>
          <input
            type="number"
            step="0.01"
            min="0.01"
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="receive-amount"
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
          />
          <div className="font-mono text-[10px] text-ink-subtle mt-1">
            Enter less than ₹{outstanding.toLocaleString("en-IN")} to leave a SHORT balance.
          </div>
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
            {isAdmin ? "Received On" : "Received On (today)"}
          </label>
          <input
            type="date"
            required
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="received-date"
            min={isAdmin ? undefined : today}
            max={isAdmin ? undefined : today}
            className={`w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink ${
              isAdmin ? "" : "opacity-70 cursor-not-allowed"
            }`}
          />
        </div>
        {kind === "exchange" && (
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Payment Method
            </label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              data-testid="received-method"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            >
              <option value="cash">Cash (counts immediately)</option>
              <option value="upi">UPI (needs verification)</option>
              <option value="online">Online / NEFT (needs verification)</option>
              <option value="cheque">Cheque (needs verification)</option>
              <option value="card">Card (needs verification)</option>
            </select>
            <div className="font-mono text-[10px] text-ink-subtle mt-1">
              Non-cash receipts stay pending until an approver verifies them in Accounts → Pending Due Verifications.
            </div>
          </div>
        )}
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
            Bank Reference / UTR {kind === "exchange" && method !== "cash" && <span className="text-destructive">*</span>}
          </label>
          <input
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            placeholder="UTR / Cheque no / Bank txn id"
            data-testid="received-ref"
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 border border-canvas-line text-xs hover:border-ink"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="confirm-received"
            className="px-3 py-1.5 bg-ink text-white text-xs font-semibold hover:bg-brand"
          >
            {saving ? "Saving…" : isTopUp ? "Book Top-up" : "Mark Received"}
          </button>
        </div>
      </form>
    </div>
  );
}

export function ReceivablesTab({ kind, branchId: filterBranchId }) {
  // kind = "finance" | "exchange"
  // filterBranchId = optional drill-down for admin (e.g. from Reports FilterBar)
  const { user } = useAuth();
  const canReconcile = (user?.permissions || []).includes("accounts.reconcile");
  // Exchange receipts are intentionally open to any signed-in staff — the
  // downstream verification queue guards misuse. Finance receipts stay
  // gated on accounts.reconcile because there's no verification step.
  const canReceive = kind === "exchange" ? true : canReconcile;
  const [data, setData] = useState({ rows: [], totals: {} });
  const [statusFilter, setStatusFilter] = useState("pending");
  // Local branch + date filters — apply to both the row list AND the
  // KPI tiles (Pending / Short / Received) so the numbers always match
  // what's on-screen.
  const [localBranch, setLocalBranch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [activeRow, setActiveRow] = useState(null);

  const endpoint = kind === "finance" ? "finance-receivables" : "exchange-receivables";

  const load = useCallback(async () => {
    try {
      const params = statusFilter === "all" ? {} : { status: statusFilter };
      // Priority: local branch filter (tab-scoped) > page-level branch filter.
      const bid = localBranch || filterBranchId;
      if (bid) params.branch_id = bid;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data: res } = await api.get(`/accounts/${endpoint}`, { params });
      setData(res);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [statusFilter, endpoint, filterBranchId, localBranch, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const revert = async (saleId) => {
    if (!window.confirm("Reset this receivable to pending (clears amount received)?")) return;
    try {
      await api.post(`/accounts/${endpoint}/${saleId}/revert`);
      toast.success("Reverted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const t = data.totals || {};
  const _todayIso = () => new Date().toISOString().slice(0, 10);
  const _daysAgoIso = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
  return (
    <div className="space-y-4">
      {/* Filter bar — Branch + Date range. Feeds both the KPI tiles
          and the row list, so operators drilling into a specific
          branch/date always see consistent numbers. */}
      <div className="flex items-center gap-3 flex-wrap px-1" data-testid={`${kind}-filter-bar`}>
        <BranchFilter
          value={localBranch}
          onChange={setLocalBranch}
          testId={`${kind}-branch-filter`}
          kind="sales"
        />
        <div className="flex items-center gap-2">
          <label className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Sale Date</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            data-testid={`${kind}-date-from`}
            className="px-2 py-1 bg-canvas border border-canvas-line text-xs font-mono"
            title="Sale date from"
          />
          <span className="text-ink-muted text-xs">→</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            data-testid={`${kind}-date-to`}
            className="px-2 py-1 bg-canvas border border-canvas-line text-xs font-mono"
            title="Sale date to"
          />
          <button
            onClick={() => { setDateFrom(_daysAgoIso(30)); setDateTo(_todayIso()); }}
            data-testid={`${kind}-date-preset-30d`}
            className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider text-ink-muted hover:border-ink hover:text-ink"
          >
            30d
          </button>
          <button
            onClick={() => { setDateFrom(_daysAgoIso(90)); setDateTo(_todayIso()); }}
            data-testid={`${kind}-date-preset-90d`}
            className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider text-ink-muted hover:border-ink hover:text-ink"
          >
            90d
          </button>
          <button
            onClick={() => { setDateFrom(""); setDateTo(""); }}
            data-testid={`${kind}-date-preset-all`}
            className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider text-ink-muted hover:border-ink hover:text-ink"
            title="All time"
          >
            All
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatPill
          label={`Pending YTD (${t.ytd_pending_count || 0})`}
          value={fmtMoney(t.ytd_pending_amount || 0)}
          accent="border-destructive bg-destructive/5 text-ink"
          testId={`${kind}-ytd-pending`}
        />
        <StatPill
          label={`Short YTD (${t.ytd_short_count || 0})`}
          value={fmtMoney(t.ytd_short_amount || 0)}
          accent="border-[#E08600] bg-[#FFF6E5] text-ink"
          testId={`${kind}-ytd-short`}
        />
        <StatPill
          label={`Received YTD (${t.ytd_received_count || 0})`}
          value={fmtMoney(t.ytd_received_amount || 0)}
          accent="border-[#00B341] bg-[#00B341]/5 text-ink"
          testId={`${kind}-ytd-received`}
        />
        <StatPill
          label="All-Time Outstanding"
          value={fmtMoney((t.pending_amount || 0) + (t.short_amount || 0))}
          accent="border-canvas-line bg-canvas-card text-ink"
          testId={`${kind}-outstanding-total`}
        />
      </div>

      {data.fy_window && (
        <div className="font-mono text-[10px] text-ink-subtle uppercase tracking-wider">
          YTD window: {data.fy_window.from} → {data.fy_window.to}
        </div>
      )}

      <AgingStrip aging={t.aging} kind={kind} />

      <div className="flex border border-canvas-line w-fit">
        {[
          { v: "pending", l: "Pending" },
          { v: "short", l: "Short" },
          { v: "received", l: "Received" },
          { v: "all", l: "All" },
        ].map((opt) => (
          <button
            key={opt.v}
            onClick={() => setStatusFilter(opt.v)}
            data-testid={`${kind}-filter-${opt.v}`}
            className={`px-4 py-2 text-xs font-mono uppercase tracking-wider ${
              statusFilter === opt.v ? "bg-ink text-white" : "text-ink hover:bg-canvas-panel"
            }`}
          >
            {opt.l}
          </button>
        ))}
      </div>

      <div className="bg-canvas-card border border-canvas-line overflow-x-auto">
        <table className="w-full text-sm" data-testid={`${kind}-table`}>
          <thead className="border-b border-canvas-line bg-canvas-panel">
            <tr>
              {(kind === "finance"
                ? [
                    { l: "Branch" }, { l: "Sale date" }, { l: "Customer" }, { l: "Vehicle" },
                    { l: "Financier" }, { l: "DO #" },
                    { l: "Expected ₹", r: true }, { l: "Received ₹", r: true }, { l: "Short ₹", r: true }, { l: "Commission ₹", r: true },
                    { l: "Status" }, { l: "Age" }, { l: "Last on" }, { l: "UTR / Ref" }, { l: "", r: true },
                  ]
                : [
                    { l: "Branch" }, { l: "Sale date" }, { l: "Customer" }, { l: "Vehicle" },
                    { l: "Trade-in" }, { l: "Reg #" }, { l: "Evaluator" },
                    { l: "Expected ₹", r: true }, { l: "Received ₹", r: true }, { l: "Short ₹", r: true },
                    { l: "Status" }, { l: "Age" }, { l: "Last on" }, { l: "UTR / Ref" }, { l: "", r: true },
                  ]
              ).map((h, i) => (
                <th
                  key={`${h.l}-${i}`}
                  className={`px-3 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted ${h.r ? "text-right" : "text-left"}`}
                >
                  {h.l}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && (
              <tr>
                <td colSpan={kind === "finance" ? 15 : 15} className="px-3 py-10 text-center font-mono text-xs text-ink-subtle">
                  Nothing here. {statusFilter !== "all" && `Try "All" filter.`}
                </td>
              </tr>
            )}
            {data.rows.map((r) => (
              <tr key={r.sale_id} data-testid={`${kind}-row-${r.sale_id}`} className="border-b border-canvas-line last:border-b-0">
                <td className="px-3 py-3 font-mono text-[10px] uppercase tracking-wider" data-testid={`${kind}-branch-${r.sale_id}`}>
                  {r.branch_name || "—"}
                </td>
                <td className="px-3 py-3 font-mono text-[11px]">{fmtDate(r.sale_date)}</td>
                <td className="px-3 py-3">{r.customer?.name || "—"}</td>
                <td className="px-3 py-3 text-xs">
                  {r.vehicle ? vehicleLabel(r.vehicle) : "—"}
                  {r.vehicle?.vin && (
                    <div className="font-mono text-[10px] text-ink-subtle">{r.vehicle.vin}</div>
                  )}
                </td>
                {kind === "finance" ? (
                  <>
                    <td className="px-3 py-3 font-semibold">{r.financier_name || "—"}</td>
                    <td className="px-3 py-3 font-mono text-[11px]">{r.do_number || "—"}</td>
                    <td className="px-3 py-3 text-right font-mono tabular-nums">{fmtMoney(r.finance_amount)}</td>
                  </>
                ) : (
                  <>
                    <td className="px-3 py-3 text-xs">{r.exchange_description || r.exchange_make_model || "—"}</td>
                    <td className="px-3 py-3 font-mono text-[11px]">{r.exchange_registration_no || "—"}</td>
                    <td className="px-3 py-3 text-xs">{r.evaluator_name || "—"}</td>
                    <td className="px-3 py-3 text-right font-mono tabular-nums">{fmtMoney(r.exchange_amount)}</td>
                  </>
                )}
                <td className="px-3 py-3 text-right font-mono tabular-nums text-[#00713a]">{r.amount_received > 0 ? fmtMoney(r.amount_received) : "—"}</td>
                <td className="px-3 py-3 text-right font-mono tabular-nums text-[#A35700]">{r.short_amount > 0 ? fmtMoney(r.short_amount) : "—"}</td>
                {kind === "finance" && (
                  <td className="px-3 py-3 text-right font-mono tabular-nums">{fmtMoney(r.commission_amount)}</td>
                )}
                <td className="px-3 py-3">
                  <StatusPill status={r.status} testId={`${kind}-status-${r.sale_id}`} />
                </td>
                <td className="px-3 py-3">
                  {r.status === "received" ? (
                    <span className="font-mono text-[10px] text-ink-subtle">—</span>
                  ) : (
                    <AgingPill
                      days={r.days_since_sale ?? 0}
                      bucket={r.aging_bucket || "0-30"}
                      testId={`${kind}-age-${r.sale_id}`}
                    />
                  )}
                </td>
                <td className="px-3 py-3 font-mono text-[11px]">{r.received_on ? fmtDate(r.received_on) : "—"}</td>
                <td className="px-3 py-3 font-mono text-[11px]">{r.bank_reference || "—"}</td>
                <td className="px-3 py-3 text-right">
                  {r.status === "received" ? (
                    canReconcile ? (
                      <button
                        onClick={() => revert(r.sale_id)}
                        data-testid={`${kind}-revert-${r.sale_id}`}
                        className="p-1.5 border border-canvas-line hover:border-ink"
                        title="Reset to pending"
                      >
                        <ArrowCounterClockwise size={13} weight="bold" />
                      </button>
                    ) : (
                      <span className="text-[10px] font-mono text-ink-subtle uppercase tracking-wider" title="Only Accounts can revert">received</span>
                    )
                  ) : canReceive ? (
                    <button
                      onClick={() => setActiveRow(r)}
                      data-testid={`${kind}-receive-${r.sale_id}`}
                      className="px-2.5 py-1.5 bg-ink text-white text-xs font-semibold hover:bg-brand inline-flex items-center gap-1"
                    >
                      {r.status === "short" ? <><Plus size={11} weight="bold" /> Top-up</> : "Mark received"}
                    </button>
                  ) : (
                    <span
                      className="text-[10px] font-mono text-ink-subtle uppercase tracking-wider"
                      title="Only the Accounts team can record receipts. Contact accounts to reconcile this line."
                      data-testid={`${kind}-locked-${r.sale_id}`}
                    >
                      accounts-only
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {activeRow && (
        <ReceiveDialog
          row={activeRow}
          kind={kind}
          onClose={() => setActiveRow(null)}
          onSaved={() => {
            setActiveRow(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function CommissionsTab() {
  const [data, setData] = useState({ by_financier: [], rows: [], totals: {} });
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [financierId, setFinancierId] = useState("");
  const [branchId, setBranchId] = useState("");

  const load = useCallback(async () => {
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (financierId) params.financier_id = financierId;
      if (branchId) params.branch_id = branchId;
      const { data: res } = await api.get("/accounts/commissions", { params });
      setData(res);
      if (!dateFrom && res.period) setDateFrom(res.period.from);
      if (!dateTo && res.period) setDateTo(res.period.to);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [dateFrom, dateTo, financierId, branchId]);

  useEffect(() => {
    // Re-fetch whenever financier/branch filter toggles; date filters use the Apply button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    load();
  }, [financierId, branchId]);

  return (
    <div className="space-y-4" data-testid="commissions-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatPill
          label={`Commission ${data.totals.total_count || 0} sales`}
          value={fmtMoney(data.totals.total_commission || 0)}
          accent="border-[#00B341] bg-[#00B341]/5 text-ink"
          testId="commissions-total"
        />
        <div className="md:col-span-3 flex items-end gap-2 flex-wrap">
          <BranchFilter
            value={branchId}
            onChange={setBranchId}
            data-testid="commissions-branch-filter"
          />
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="commissions-from"
              className="px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              data-testid="commissions-to"
              className="px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            />
          </div>
          <button
            onClick={load}
            data-testid="commissions-apply"
            className="px-4 py-2 bg-ink text-white text-xs font-semibold hover:bg-brand"
          >
            Apply
          </button>
          {financierId && (
            <button
              onClick={() => setFinancierId("")}
              data-testid="commissions-clear-financier"
              className="px-4 py-2 border border-canvas-line text-xs hover:border-ink"
            >
              Clear financier filter
            </button>
          )}
        </div>
      </div>

      <div className="bg-canvas-card border border-canvas-line">
        <div className="px-4 py-3 border-b border-canvas-line">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">
            By Financier
          </div>
          <h3 className="font-display text-base font-semibold text-ink mt-1">
            Commissions grouped {financierId ? "· filtered" : "· YTD"}
          </h3>
        </div>
        <table className="w-full text-sm" data-testid="commissions-by-financier">
          <thead className="border-b border-canvas-line bg-canvas-panel">
            <tr>
              {[
                { l: "Financier" }, { l: "Branch" },
                { l: "# Sales", r: true }, { l: "Finance Volume", r: true }, { l: "Commission Earned", r: true },
                { l: "", r: true },
              ].map((h, i) => (
                <th
                  key={`${h.l}-${i}`}
                  className={`px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted ${h.r ? "text-right" : "text-left"}`}
                >
                  {h.l}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.by_financier.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center font-mono text-xs text-ink-subtle">
                  No commissions in this period.
                </td>
              </tr>
            ) : (
              data.by_financier.map((g) => (
                <tr key={`${g.financier_id}-${g.branch_id}`} data-testid={`commission-group-${g.financier_id}-${g.branch_id}`} className="border-b border-canvas-line last:border-b-0">
                  <td className="px-3 py-2 font-semibold">{g.financier_name}</td>
                  <td className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider">{g.branch_name || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono text-[11px]">{g.count}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMoney(g.total_finance_amount)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold text-[#00713a]">{fmtMoney(g.total_commission)}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => setFinancierId(financierId === g.financier_id ? "" : g.financier_id)}
                      className="font-mono text-[10px] uppercase tracking-wider text-ink-muted hover:text-ink"
                      data-testid={`commission-filter-${g.financier_id}-${g.branch_id}`}
                    >
                      {financierId === g.financier_id ? "Unfilter" : "Show sales"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {financierId && (
        <div className="bg-canvas-card border border-canvas-line overflow-x-auto">
          <div className="px-4 py-3 border-b border-canvas-line font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">
            Sale-Level Commissions
          </div>
          <table className="w-full text-sm" data-testid="commissions-lines">
            <thead className="border-b border-canvas-line bg-canvas-panel">
              <tr>
                {[
                  { l: "Branch" }, { l: "Sale date" }, { l: "Customer" }, { l: "Vehicle" }, { l: "DO #" },
                  { l: "Finance ₹", r: true }, { l: "Commission ₹", r: true },
                ].map((h, i) => (
                  <th
                    key={`${h.l}-${i}`}
                    className={`px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted ${h.r ? "text-right" : "text-left"}`}
                  >
                    {h.l}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.sale_id} className="border-b border-canvas-line last:border-b-0">
                  <td className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider">{r.branch_name || "—"}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{fmtDate(r.sale_date)}</td>
                  <td className="px-3 py-2">{r.customer?.name || "—"}</td>
                  <td className="px-3 py-2 text-xs">{r.vehicle ? vehicleLabel(r.vehicle) : "—"}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{r.do_number || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMoney(r.finance_amount)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold text-[#00713a]">{fmtMoney(r.commission_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CardSurchargesTab() {
  const [data, setData] = useState(null);
  const [branchId, setBranchId] = useState("");

  const load = useCallback(async () => {
    try {
      const params = branchId ? { branch_id: branchId } : {};
      const { data: res } = await api.get("/accounts/card-surcharges/summary", { params });
      setData(res);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [branchId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) return <div className="text-sm text-ink-muted">Loading…</div>;

  const rows = [
    { label: "Sales", key: "sales" },
    { label: "Bookings", key: "bookings" },
    { label: "Vouchers", key: "vouchers" },
  ];

  return (
    <div className="space-y-4" data-testid="card-surcharges-tab">
      <div className="flex items-end gap-3 flex-wrap">
        <BranchFilter value={branchId} onChange={setBranchId} data-testid="surcharge-branch-filter" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <StatPill
          label="Card Surcharge YTD"
          value={fmtMoney(data.total_fy || 0)}
          accent="border-ink bg-canvas-card text-ink"
          testId="surcharge-ytd"
        />
        <StatPill
          label="All-Time Surcharges"
          value={fmtMoney(data.total_all_time || 0)}
          accent="border-canvas-line bg-canvas-card text-ink"
          testId="surcharge-alltime"
        />
      </div>

      {data.fy_window && (
        <div className="font-mono text-[10px] text-ink-subtle uppercase tracking-wider">
          YTD window: {data.fy_window.from} → {data.fy_window.to}
        </div>
      )}

      <div className="bg-canvas-card border border-canvas-line">
        <div className="px-4 py-3 border-b border-canvas-line">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">
            By Source
          </div>
          <h3 className="font-display text-base font-semibold text-ink mt-1">
            Swipe-Surcharge Recovered
          </h3>
          <p className="text-xs text-ink-muted mt-1">
            Use this to reconcile against the swipe-fee total on your bank statement.
          </p>
        </div>
        <table className="w-full text-sm" data-testid="surcharges-table">
          <thead className="border-b border-canvas-line bg-canvas-panel">
            <tr>
              {[
                { l: "Source" },
                { l: "YTD ₹", r: true }, { l: "YTD swipes", r: true },
                { l: "All-time ₹", r: true }, { l: "All-time swipes", r: true },
              ].map((h, i) => (
                <th
                  key={`${h.l}-${i}`}
                  className={`px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted ${h.r ? "text-right" : "text-left"}`}
                >
                  {h.l}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const b = data[r.key] || {};
              return (
                <tr
                  key={r.key}
                  data-testid={`surcharge-row-${r.key}`}
                  className="border-b border-canvas-line last:border-b-0"
                >
                  <td className="px-3 py-2 font-semibold">{r.label}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">
                    {fmtMoney(b.fy || 0)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{b.fy_count || 0}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">
                    {fmtMoney(b.all_time || 0)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">
                    {b.all_time_count || 0}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* By Branch — shows which branch is generating swipe fees, so the
          accountant can drill into a single location during reconciliation. */}
      {(data.by_branch || []).length > 0 && (
        <div className="bg-canvas-card border border-canvas-line">
          <div className="px-4 py-3 border-b border-canvas-line">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">By Branch</div>
            <h3 className="font-display text-base font-semibold text-ink mt-1">Swipe-Surcharge Recovered by Branch</h3>
          </div>
          <table className="w-full text-sm" data-testid="surcharges-by-branch">
            <thead className="border-b border-canvas-line bg-canvas-panel">
              <tr>
                {[
                  { l: "Branch" },
                  { l: "Sales ₹ (FY)", r: true },
                  { l: "Bookings ₹ (FY)", r: true },
                  { l: "Vouchers ₹ (FY)", r: true },
                  { l: "Total FY ₹", r: true },
                  { l: "Total All-time ₹", r: true },
                ].map((h, i) => (
                  <th
                    key={`${h.l}-${i}`}
                    className={`px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted ${h.r ? "text-right" : "text-left"}`}
                  >
                    {h.l}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.by_branch.map((b) => (
                <tr
                  key={b.branch_id || "unbranded"}
                  data-testid={`surcharge-branch-${b.branch_id}`}
                  className="border-b border-canvas-line last:border-b-0"
                >
                  <td className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider">{b.branch_name || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMoney(b.sales?.fy || 0)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMoney(b.bookings?.fy || 0)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMoney(b.vouchers?.fy || 0)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold">{fmtMoney(b.total_fy || 0)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMoney(b.total_all_time || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RetailCashDueTab() {
  const [data, setData] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [branchId, setBranchId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = branchId ? { branch_id: branchId } : {};
      const { data: res } = await api.get("/accounts/retail-cash-due", { params });
      setData(res);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    load();
  }, [load]);

  const t = data.totals || {};
  return (
    <div className="space-y-4" data-testid="retail-cash-due-tab">
      <div className="flex items-end gap-3 flex-wrap">
        <BranchFilter value={branchId} onChange={setBranchId} data-testid="retail-cash-branch-filter" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatPill
          label={`Outstanding (${t.count || 0})`}
          value={fmtMoney(t.outstanding_amount || 0)}
          accent="border-destructive bg-destructive/5 text-ink"
          testId="retail-cash-outstanding"
        />
        <StatPill
          label={`YTD Outstanding (${t.ytd_count || 0})`}
          value={fmtMoney(t.ytd_outstanding_amount || 0)}
          accent="border-[#E08600] bg-[#FFF6E5] text-ink"
          testId="retail-cash-ytd"
        />
        <StatPill
          label="Write-offs"
          value="Excluded"
          accent="border-canvas-line bg-canvas-card text-ink-muted"
          testId="retail-cash-writeoffs-excluded"
        />
      </div>

      {data.fy_window && (
        <div className="font-mono text-[10px] text-ink-subtle uppercase tracking-wider">
          YTD window: {data.fy_window.from} → {data.fy_window.to}
          <span className="ml-3 text-ink-muted normal-case tracking-normal">
            (Reconciled write-offs are removed from this list automatically.)
          </span>
        </div>
      )}

      <AgingStrip aging={t.aging} kind="retail-cash" />

      <div className="bg-canvas-card border border-canvas-line overflow-x-auto">
        <table className="w-full text-sm" data-testid="retail-cash-table">
          <thead className="border-b border-canvas-line bg-canvas-panel">
            <tr>
              {[
                { l: "Branch" }, { l: "Sale date" }, { l: "Invoice" }, { l: "Customer" }, { l: "Vehicle" },
                { l: "On-road \u20b9", r: true }, { l: "Advance \u20b9", r: true }, { l: "Tenders \u20b9", r: true }, { l: "Discount \u20b9", r: true },
                { l: "Finance \u20b9", r: true }, { l: "Exchange \u20b9", r: true }, { l: "Cash Due \u20b9", r: true },
                { l: "Age" },
              ].map((h, i) => (
                <th
                  key={`${h.l}-${i}`}
                  className={`px-3 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted ${h.r ? "text-right" : "text-left"}`}
                >
                  {h.l}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={13} className="px-3 py-10 text-center font-mono text-xs text-ink-subtle">
                  Loading\u2026
                </td>
              </tr>
            )}
            {!loading && data.rows.length === 0 && (
              <tr>
                <td colSpan={13} className="px-3 py-10 text-center font-mono text-xs text-ink-subtle">
                  No outstanding cash dues. All sales are settled (or written off).
                </td>
              </tr>
            )}
            {!loading && data.rows.map((r) => (
              <tr key={r.sale_id} data-testid={`retail-cash-row-${r.sale_id}`} className="border-b border-canvas-line last:border-b-0">
                <td className="px-3 py-3 font-mono text-[10px] uppercase tracking-wider">{r.branch_name || "\u2014"}</td>
                <td className="px-3 py-3 font-mono text-[11px]">{fmtDate(r.sale_date)}</td>
                <td className="px-3 py-3 font-mono text-[11px]">{r.invoice_no || "\u2014"}</td>
                <td className="px-3 py-3">
                  {r.customer?.name || "\u2014"}
                  {r.customer?.phone && (
                    <div className="font-mono text-[10px] text-ink-subtle">{r.customer.phone}</div>
                  )}
                </td>
                <td className="px-3 py-3 text-xs">
                  {r.vehicle ? vehicleLabel(r.vehicle) || "\u2014" : "\u2014"}
                  {r.vehicle?.vin && (
                    <div className="font-mono text-[10px] text-ink-subtle">{r.vehicle.vin}</div>
                  )}
                </td>
                <td className="px-3 py-3 text-right font-mono tabular-nums">{fmtMoney(r.on_road_price)}</td>
                <td className="px-3 py-3 text-right font-mono tabular-nums">{r.advance_amount > 0 ? fmtMoney(r.advance_amount) : "\u2014"}</td>
                <td className="px-3 py-3 text-right font-mono tabular-nums text-[#00713a]">{r.tenders_total > 0 ? fmtMoney(r.tenders_total) : "\u2014"}</td>
                <td className="px-3 py-3 text-right font-mono tabular-nums">{r.discount > 0 ? fmtMoney(r.discount) : "\u2014"}</td>
                <td className="px-3 py-3 text-right font-mono tabular-nums">{r.finance_amount > 0 ? fmtMoney(r.finance_amount) : "\u2014"}</td>
                <td className="px-3 py-3 text-right font-mono tabular-nums">{r.exchange_amount > 0 ? fmtMoney(r.exchange_amount) : "\u2014"}</td>
                <td className="px-3 py-3 text-right font-mono tabular-nums font-semibold text-destructive">{fmtMoney(r.due)}</td>
                <td className="px-3 py-3">
                  <AgingPill days={r.days_since_sale ?? 0} bucket={r.aging_bucket || "0-30"} testId={`retail-cash-age-${r.sale_id}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}



export default function AccountsPage() {
  const [tab, setTab] = useState("finance");

  return (
    <div data-testid="accounts-page">
      <PageHeader
        eyebrow="06 / Accounts"
        title="Receivables & Commissions"
        subtitle="Track finance & exchange receivables, manage shorts, and review financier commissions."
      />
      <div className="px-8 py-6 space-y-4">
        <div className="flex border border-canvas-line w-fit" data-testid="accounts-tabs">
          {[
            { v: "finance", l: "Finance Receivables" },
            { v: "platform", l: "Platform Receivables" },
            { v: "pending-verif", l: "Pending Due Verifications" },
            { v: "commissions", l: "Commissions" },
            { v: "surcharges", l: "Card Surcharges" },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => setTab(opt.v)}
              data-testid={`accounts-tab-${opt.v}`}
              className={`px-4 py-2 text-xs font-mono uppercase tracking-wider ${
                tab === opt.v ? "bg-ink text-white" : "text-ink hover:bg-canvas-panel"
              }`}
            >
              {opt.l}
            </button>
          ))}
        </div>

        {tab === "finance" && <ReceivablesTab kind="finance" />}
        {tab === "platform" && <PlatformReceivablesTab />}
        {tab === "pending-verif" && <PendingCollectionVerifications />}
        {tab === "commissions" && <CommissionsTab />}
        {tab === "surcharges" && <CardSurchargesTab />}
      </div>
    </div>
  );
}
